// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import { Dictionary } from "@insite/client-framework/Common/Types";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { LinkModelWithIcon } from "@toolkit/Framework/Store/Links/LinksSelectors";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import PinLine, { PinLineOffset } from "@toolkit/Library/Components/Icons/Pin/PinLine";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
/* Bunzl Custom Code - Start */
import iconsIndex from "@store/Framework/Common/Hooks/IconIndex/iconsIndex";
import { IconKey } from "@store/Framework/Common/Hooks/IconIndex/useIconIndex";
import HeaderLinkListItem, { LinkIconPropsWithoutSrc } from "@store/Library/Components/Header/HeaderLinkListItem";
/* Bunzl Custom Code - End */

export interface HeaderLinkListIcon {
    displayName: string;
    src: IconPresentationProps["src"];
    size?: string;
    offset?: IconOffset;
}
/* Bunzl Custom Code */
interface LinkFieldsWithIconKey extends Omit<LinkModelWithIcon, "fields"> {
    fields: Omit<LinkModelWithIcon["fields"], "icon" & "position"> & {
        icon: IconKey;
        position?: "right" | "left";
    };
}
export const headerLinkListIcons: Dictionary<HeaderLinkListIcon> = {
    location: {
        displayName: "Location",
        size: pxToRem(20),
        src: PinLine,
        offset: PinLineOffset,
    },
};
/* Valtech Custom Code */

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkFieldsWithIconKey[]; // Bunzl Custom Code
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // Valtech Custom Code
    linkWrapper?: InjectableCss;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;
            height: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;
        `,
    },
    link: {
        icon: {
            iconProps: {
                color: "inherit",
            },
        },
        css: linkStyles("inherit", "menu.secondaryNavigation.hover", false, true),
    },
};

/* Valtech Custom Code */
const defaultCustomCss = `& {
}

.list-link {
}
`;
/* Valtech Custom Code */

export const HeaderLinkList: React.FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);

    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (links.length < 1) {
        return null;
    }

    /* Valtech Custom Code */
    const customCssWrapper = css`
        ${styles.headerLinkListWrapper?.css}
        ${fields.customCSS}
    `;

    return (
        <StyledWrapper {...styles.headerLinkListWrapper} css={customCssWrapper}>
            {links.map((link, index) => {
                if (!link.url) {
                    return null;
                }

                const linkfields = fields.links[index].fields;

                const title = linkfields.overriddenTitle || link.title;
                const iconData = headerLinkListIcons[linkfields.icon] || {};
                const spacingSide = linkfields.iconPosition === "left" ? "right" : "left";
                // Bunzl Custom Code --->
                const icon = linkfields.icon;
                const iconProps: LinkIconPropsWithoutSrc | undefined = mergeProps<LinkPresentationProps["icon"]>(
                    styles.link?.icon,
                    {
                        position: linkfields.iconPosition,
                        iconProps: {
                            css: css`
                                ${iconScalableStyles(iconData.size, iconData.offset)}
                                margin-${spacingSide}: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
                            `,
                        },
                    },
                );

                return (
                    <StyledWrapper key={`${link.url}.${title}`} {...styles.linkWrapper}>
                        <HeaderLinkListItem
                            iconKey={icon}
                            iconPropsWithoutSource={iconProps}
                            href={link.url}
                            {...styles.link}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                        >
                            {title}
                        </HeaderLinkListItem>
                    </StyledWrapper>
                    // <--- Bunzl Custom Code
                );
            })}
        </StyledWrapper>
    );
    /* Valtech Custom Code */
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
// Bunzl Custom Code
const iconOptions = Object.keys(iconsIndex)
    .sort()
    .map(icon => ({
        displayName: icon,
        value: icon,
    }));
const widgetModule: WidgetModule = {
    component: HeaderLinkList,
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable", // Valtech Custom Code
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    /* Valtech Custom Code */
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        editorTemplate: "DropDownField",
                        // Bunzl Custom Code
                        options: iconOptions,
                        defaultValue: "",
                    },
                    {
                        name: "iconPosition",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "left",
                        isRequired: true,
                        isVisible: x => x.fields.icon,
                    },
                    /* Valtech Custom Code */
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
